import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { profileAPI, workspacesAPI } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useView } from '@/context/ViewContext';
import type { Workspace } from '@/types';
import { ROLE_COLORS } from './components/profileConstants';
import type { ProfileData, ActivityEntry } from './components/types';
import IdentityHeader from './components/IdentityHeader';
import AccountDetailsCard from './components/AccountDetailsCard';
import WorkspaceAccessCard from './components/WorkspaceAccessCard';
import DashboardViewCard from './components/DashboardViewCard';
import SecurityCard from './components/SecurityCard';
import RecentActivityCard from './components/RecentActivityCard';
import DelayedLoading from '@/components/ui/DelayedLoading';
import LoadingStatus from '@/components/ui/LoadingStatus';

const ProfilePage: React.FC = () => {
  const { updateUser } = useAuth();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Activity log
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityOffset, setActivityOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const ACTIVITY_LIMIT = 20;

  // Workspace access + active-workspace selection (moved here from the nav bar)
  const { currentWorkspace, availableWorkspaces, setCurrentWorkspace, setAvailableWorkspaces } = useWorkspace();
  const queryClient = useQueryClient();

  // Dashboard "view as" role (admin only) — moved here from the nav bar.
  const { currentViewRole, setCurrentViewRole, canSwitchRoles } = useView();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await profileAPI.getMe();
        setProfile(res.data);
        // Sync auth context with fresh data
        if (res.data) {
          updateUser({
            id: res.data.id,
            name: res.data.name,
            email: res.data.email,
            role: res.data.role as 'admin' | 'scout' | 'coach' | 'medical' | 'manager',
            createdAt: res.data.createdAt,
            updatedAt: res.data.updatedAt,
          });
        }
      } catch {
        // handled by interceptor
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchActivity = useCallback(async (offset: number, append: boolean) => {
    setActivityLoading(true);
    try {
      const res = await profileAPI.getActivity(ACTIVITY_LIMIT, offset);
      const items = res.data || [];
      if (append) {
        setActivity(prev => [...prev, ...items]);
      } else {
        setActivity(items);
      }
      setHasMore(items.length === ACTIVITY_LIMIT);
    } catch {
      // silently fail
    } finally {
      setActivityLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivity(0, false);
  }, [fetchActivity]);

  // Refresh the list of accessible workspaces (with stats) and sync it into the
  // shared context (also persists to localStorage) so it stays current.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await workspacesAPI.list();
        if (!cancelled && list) setAvailableWorkspaces(list);
      } catch {
        // non-critical; context already holds cached workspaces
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setAvailableWorkspaces]);

  const handleSelectWorkspace = (ws: Workspace) => {
    if (ws.id === currentWorkspace?.id) return;
    setCurrentWorkspace(ws);
    // Invalidate cached queries so all data re-fetches for the new workspace.
    queryClient.invalidateQueries();
  };

  const loadMore = () => {
    const newOffset = activityOffset + ACTIVITY_LIMIT;
    setActivityOffset(newOffset);
    fetchActivity(newOffset, true);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setChangingPassword(true);
    try {
      await profileAPI.changePassword(currentPassword, newPassword);
      setPasswordSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      // Refresh activity so "Last Password Change" reflects the update.
      fetchActivity(0, false);
      setActivityOffset(0);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to change password';
      setPasswordError(msg);
    } finally {
      setChangingPassword(false);
    }
  };

  const formatDate = (iso: string) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (iso: string) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // "Last Login" is an authoritative account fact tracked on the user record,
  // not reconstructed from the (paginated) activity feed.
  const lastLogin = profile?.lastLogin ?? '';
  const lastPasswordChange = useMemo(
    () => activity.find(a => a.action === 'password_change')?.createdAt ?? '',
    [activity],
  );

  const confirmMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  if (loading) {
    return (
      <DelayedLoading loading>
        <LoadingStatus variant="panel" label="Loading profile" className="py-16" />
      </DelayedLoading>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-red-500">Failed to load profile</div>
      </div>
    );
  }

  const roleColor = ROLE_COLORS[profile.role] || 'bg-gray-100 text-gray-700 ring-gray-500/20 dark:bg-gray-700 dark:text-gray-200';

  // Players are locked to their squad's workspace, so all workspace details and the
  // workspace switcher are hidden from their profile.
  const isPlayer = profile.role === 'player';

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* ── Identity header ───────────────────────────────────────────── */}
      <IdentityHeader profile={profile} roleColor={roleColor} currentWorkspace={isPlayer ? null : currentWorkspace} />

      {/* ── Account details (two-column) ──────────────────────────────── */}
      <AccountDetailsCard
        profile={profile}
        roleColor={roleColor}
        lastLogin={lastLogin}
        workspaceCount={availableWorkspaces.length}
        showWorkspaces={!isPlayer}
        formatDate={formatDate}
        formatDateTime={formatDateTime}
      />

      {/* ── Workspace access (active-workspace selector) — staff only ─── */}
      {!isPlayer && (
        <WorkspaceAccessCard
          availableWorkspaces={availableWorkspaces}
          currentWorkspace={currentWorkspace}
          onSelectWorkspace={handleSelectWorkspace}
        />
      )}

      {/* ── Dashboard view (admin "view as" — moved here from the nav bar) ─ */}
      {canSwitchRoles && (
        <DashboardViewCard currentViewRole={currentViewRole} onSelectViewRole={setCurrentViewRole} />
      )}

      {/* ── Security ──────────────────────────────────────────────────── */}
      <SecurityCard
        lastPasswordChange={lastPasswordChange}
        formatDate={formatDate}
        currentPassword={currentPassword}
        setCurrentPassword={setCurrentPassword}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        confirmPassword={confirmPassword}
        setConfirmPassword={setConfirmPassword}
        passwordError={passwordError}
        passwordSuccess={passwordSuccess}
        changingPassword={changingPassword}
        confirmMismatch={confirmMismatch}
        onSubmit={handlePasswordChange}
      />

      {/* ── Recent activity ───────────────────────────────────────────── */}
      <RecentActivityCard
        activity={activity}
        activityLoading={activityLoading}
        hasMore={hasMore}
        onLoadMore={loadMore}
        formatDateTime={formatDateTime}
      />
    </div>
  );
};

export default ProfilePage;
